import React, { useState, useEffect, useMemo } from 'react'
import { supabase } from './supabaseClient'
import { C } from './theme'
import { CopyButton } from './CopyButton'
import { RefreshCw, Download, AlertTriangle, Home, Wallet, TrendingUp } from 'lucide-react'

// ─────────────────────────────────────────────────────────────────────────────
// Lucro real — factura do fornecedor contra as encomendas que ela cobre.
//
// Assenta em dois documentos e não numa cadeia de estimativas: não há nada para
// acreditar, há números para conferir. Foi por isso que substituiu o Capital.
//
// DUAS PERGUNTAS DIFERENTES, DUAS LINHAS SEPARADAS:
//
//   LUCRO       = receita das encomendas − custo da factura
//                 Quanto se ganhou. A parte da Cátia entra na receita e no custo,
//                 e como ela compra ao custo o efeito anula-se sozinho.
//
//   TESOURARIA  = dinheiro que entrou de facto − custo da factura
//                 Quanto falta pôr do bolso para pagar ao fornecedor. A Cátia
//                 não paga dinheiro real, por isso a parte dela é sempre
//                 dinheiro que sai e não entra.
//
// Misturar as duas dá disparates: abater a Cátia ao lucro fazia a factura 84
// aparecer com −262 € de prejuízo quando na verdade deu +1 009 € de lucro. Os
// 262 € existem, mas são de tesouraria, não de lucro.
//
// O IVA fica de fora dos dois lados. É o que é tributado, e o IVA cobrado ao
// cliente não é dinheiro nosso — dá uma falsa folga de tesouraria.
//
// ATENÇÃO À FASE: o valor definitivo da factura só se sabe quando os portes são
// acrescentados no envio. Enquanto está em previsão, o custo é estimado e o
// lucro também. A fase vem marcada em cada cartão.
// ─────────────────────────────────────────────────────────────────────────────

const fmt = (n) => Number(n || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// Fraccao do valor da encomenda ja cobrada. Deixou de ser 0,5 por prestacao:
// com o sinal flexivel a 1.a pode valer 8% e a 2.a 92%. Sem isto o "por cobrar"
// ficava errado por quase toda a encomenda.
const fracCobrada = (e) => {
  if (e?.pago_2a_prestacao) return 1
  if (!e?.pago_1a_prestacao) return 0
  // PAGAMENTO A 100%: a 1.a prestacao E a encomenda toda, nao ha segunda.
  //
  // Faltava aqui, e so aqui. A Tesouraria ja tratava do caso; este ecra caia no
  // `total / 2` do fim e mostrava metade de uma encomenda ja paga por inteiro
  // como estando por cobrar. Duas telas a responder a mesma pergunta com regras
  // diferentes, e a que estava errada era a do lucro.
  if (String(e?.tipo_pagamento) === '100') return 1
  const total = Number(e?.total_final ?? e?.total_com_iva ?? 0)
  if (!(total > 0)) return 0.5
  const p1 = e?.pago_1a_valor != null ? Number(e.pago_1a_valor)
           : e?.sinal_escolhido != null ? Number(e.sinal_escolhido)
           : total / 2
  return Math.min(1, p1 / total)
}

function csvEscape(v) {
  const s = String(v ?? '')
  return (s.includes(';') || s.includes('"') || s.includes('\n')) ? '"' + s.replace(/"/g, '""') + '"' : s
}

export default function LucroReal({ onVerFactura }) {
  const [compras, setCompras] = useState([])
  const [links, setLinks] = useState([])
  const [encomendas, setEncomendas] = useState([])
  const [internos, setInternos] = useState(new Set())
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState('')
  const [detalhe, setDetalhe] = useState(null)   // factura cujas contas estão abertas

  const carregar = async () => {
    setLoading(true); setErro('')
    try {
      const [cRes, lRes, eRes, pRes] = await Promise.all([
        supabase.from('compras_fornecedor')
          .select('id, numero_fatura, numero_interno, data_fatura, fase, valor_sem_iva, valor_final_sem_iva, pago_1a_prestacao, pago_2a_prestacao')
          .order('data_fatura', { ascending: false }),
        supabase.from('compras_fornecedor_encomendas')
          .select('compra_id, encomenda_id, quantidade_alocada, produto_nome, custo_unitario_previsao, custo_unitario_final'),
        supabase.from('encomendas')
          .select('id, numero_encomenda, cliente_nome, cliente_id, total_sem_iva, desconto_valor, pago_1a_prestacao, pago_2a_prestacao, pago_1a_valor, sinal_escolhido, tipo_pagamento, total_final, total_com_iva, estado, historica, encomendado_fornecedor_em, promovido_pedido_em'),
        supabase.from('perfis').select('id').eq('cliente_interno', true),
      ])
      // AS QUATRO, e nao so as compras. A RLS a bloquear nao levanta excepcao,
      // devolve zero linhas — e cada uma destas falha de maneira propria e
      // silenciosa: sem `links` toda a factura aparece com prejuizo do tamanho
      // do custo, e sem `perfis` a parte da Catia desaparece e o "do meu bolso"
      // fica menor do que e. E este ecra existe precisamente para separar essas
      // duas coisas.
      if (cRes.error) throw new Error('Compras: ' + cRes.error.message)
      if (lRes.error) throw new Error('Ligacoes compra-encomenda: ' + lRes.error.message)
      if (eRes.error) throw new Error('Encomendas: ' + eRes.error.message)
      if (pRes.error) throw new Error('Contas internas: ' + pRes.error.message)
      setCompras(cRes.data || [])
      setLinks(lRes.data || [])
      setEncomendas(eRes.data || [])
      setInternos(new Set((pRes.data || []).map(p => p.id)))
    } catch (e) { setErro(e.message || String(e)) }
    setLoading(false)
  }
  useEffect(() => { carregar() }, [])

  const facturas = useMemo(() => {
    const encPorId = new Map(encomendas.map(e => [e.id, e]))
    // Uma encomenda pode ter várias linhas na mesma factura (uma por produto);
    // para receita conta a encomenda uma vez só.
    const encsPorCompra = new Map()
    const unPorCompra = new Map()
    for (const l of links) {
      if (!encsPorCompra.has(l.compra_id)) encsPorCompra.set(l.compra_id, new Set())
      encsPorCompra.get(l.compra_id).add(l.encomenda_id)
      unPorCompra.set(l.compra_id, (unPorCompra.get(l.compra_id) || 0) + Number(l.quantidade_alocada || 0))
    }

    return compras.map(c => {
      const ehFinal = c.fase === 'final' && Number(c.valor_final_sem_iva) > 0
      const custo = ehFinal ? Number(c.valor_final_sem_iva) : Number(c.valor_sem_iva || 0)
      const ids = [...(encsPorCompra.get(c.id) || [])]
      const encs = ids.map(id => encPorId.get(id)).filter(Boolean).filter(e => e.estado !== 'cancelado')

      // Os clientes externos contam pelo valor TOTAL, mesmo que ainda só tenham
      // pago 50%. Não é optimismo: quem pagou metade não desaparece, e se
      // desaparecer a mercadoria fica cá — comprada a metade do preço, sem
      // prejuízo. O que falta receber é mostrado à parte, para se saber o que
      // ainda está por cobrar sem contaminar a conta.
      //
      // A conta interna é diferente: aí nunca entra dinheiro nenhum por esta via.
      let receita = 0, parteCatia = 0, dosClientes = 0, porCobrar = 0
      const detalhe = []
      for (const e of encs) {
        const base = Number(e.total_sem_iva || 0) - Number(e.desconto_valor || 0)
        const interno = internos.has(e.cliente_id)
        receita += base
        if (interno) {
          parteCatia += base
        } else {
          dosClientes += base
          const frac = fracCobrada(e)
          porCobrar += base * (1 - frac)
        }
        const frac = fracCobrada(e)
        detalhe.push({
          numero: e.numero_encomenda, nome: e.cliente_nome, base, interno,
          pago: (e.pago_1a_prestacao ? 1 : 0) + (e.pago_2a_prestacao ? 1 : 0),
          // Falta cobrar a este cliente. Conta como informação, não sai do total.
          porCobrar: interno ? 0 : base * (1 - frac),
        })
      }

      // O que foi comprado para a conta interna nesta factura. É a única parte
      // que precisa de explicação: é ela que separa o lucro do dinheiro. Sem
      // compra interna, o lucro é o lucro e não há contas para destrinçar.
      const linhasCatia = links
        .filter(l => l.compra_id === c.id)
        .filter(l => { const e = encPorId.get(l.encomenda_id); return e && internos.has(e.cliente_id) })
        .map(l => {
          const cu = Number(l.custo_unitario_final ?? l.custo_unitario_previsao ?? 0)
          const q = Number(l.quantidade_alocada || 0)
          return { produto: l.produto_nome, qtd: q, custoUn: cu, total: cu * q,
            encomenda: encPorId.get(l.encomenda_id)?.numero_encomenda }
        })
        .sort((a, b) => b.total - a.total)

      const lucro = receita - custo
      const falta = custo - dosClientes   // > 0 → sai do bolso
      return {
        ...c, ehFinal, custo, receita, parteCatia, dosClientes, porCobrar, lucro, falta, linhasCatia,
        unidades: unPorCompra.get(c.id) || 0,
        margem: receita > 0 ? (lucro / receita * 100) : null,
        detalhe: detalhe.sort((a, b) => (a.numero || '').localeCompare(b.numero || '')),
        nEncomendas: encs.length,
      }
    })
  }, [compras, links, encomendas, internos])

  // Encomendas já com compromisso mas ainda sem factura de fornecedor: têm
  // receita e ainda não têm custo. Entrarem na conta inflacionava o lucro.
  const semFactura = useMemo(() => {
    const ligadas = new Set(links.map(l => l.encomenda_id))
    const lista = encomendas.filter(e =>
      !e.historica && e.estado !== 'cancelado' && !ligadas.has(e.id)
      && (e.pago_1a_prestacao || e.promovido_pedido_em)
    )
    const total = lista.reduce((s, e) => s + Number(e.total_sem_iva || 0) - Number(e.desconto_valor || 0), 0)
    return { n: lista.length, total, lista }
  }, [encomendas, links])

  const totais = useMemo(() => facturas.reduce((a, f) => ({
    custo: a.custo + f.custo, receita: a.receita + f.receita,
    lucro: a.lucro + f.lucro, catia: a.catia + f.parteCatia,
    falta: a.falta + Math.max(0, f.falta),
    clientes: a.clientes + f.dosClientes, porCobrar: a.porCobrar + f.porCobrar,
  }), { custo: 0, receita: 0, lucro: 0, catia: 0, falta: 0, clientes: 0, porCobrar: 0 }), [facturas])

  const exportar = () => {
    const linhas = [
      ['LUCRO REAL POR FACTURA DE FORNECEDOR'],
      ['Valores sem IVA'],
      [''],
      ['Factura', 'Fase', 'Unidades', 'Custo', 'Receita encomendas', 'Parte Catia', 'Lucro real', 'Margem %', 'Valor dos clientes', 'Por cobrar', 'Do meu bolso'],
      ...facturas.map(f => [
        f.numero_fatura || '—', f.ehFinal ? 'final' : 'previsao', f.unidades,
        f.custo.toFixed(2), f.receita.toFixed(2), f.parteCatia.toFixed(2),
        f.lucro.toFixed(2), f.margem != null ? f.margem.toFixed(1) : '',
        f.dosClientes.toFixed(2), f.porCobrar.toFixed(2), f.falta > 0 ? f.falta.toFixed(2) : '0.00',
      ]),
      [''],
      // Onze colunas, tantas quantas o cabecalho. Tinha dez: o "do meu bolso"
      // caia debaixo de "por cobrar" e a coluna certa ficava vazia.
      //   Factura | Fase | Unidades | Custo | Receita | Catia | Lucro | Margem % | Clientes | Por cobrar | Bolso
      ['TOTAIS', '', '',
        totais.custo.toFixed(2), totais.receita.toFixed(2), totais.catia.toFixed(2),
        totais.lucro.toFixed(2), '',
        totais.clientes.toFixed(2), totais.porCobrar.toFixed(2), totais.falta.toFixed(2)],
      [''],
      ['Encomendas ainda sem factura de fornecedor', String(semFactura.n), semFactura.total.toFixed(2), '(receita sem custo — nao entra nas contas acima)'],
    ]
    const csv = '﻿' + linhas.map(l => l.map(csvEscape).join(';')).join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }))
    const a = document.createElement('a'); a.href = url; a.download = 'lucro-real.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: C.textMuted, fontSize: '13px' }}>A carregar…</div>

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <p style={{ margin: 0, fontSize: '12px', color: C.textMuted, maxWidth: '620px', lineHeight: 1.6 }}>
          Cada factura do fornecedor contra as encomendas que ela cobre. <strong>Valores sem IVA.</strong><br />
          <strong style={{ color: C.text }}>Lucro</strong> = receita − custo · <strong style={{ color: C.text }}>Tesouraria</strong> = o que falta pôr do bolso para pagar ao fornecedor. São perguntas diferentes.
        </p>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button onClick={carregar} style={btnSec}><RefreshCw size={12} /> Actualizar</button>
          <button onClick={exportar} style={btnPri}><Download size={12} /> CSV</button>
        </div>
      </div>

      {erro && <div style={{ padding: '10px 14px', background: C.dangerSoft, border: `1px solid ${C.danger}55`, borderRadius: '4px', color: C.danger, fontSize: '12px', marginBottom: '12px' }}>{erro}</div>}

      {/* Totais */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '14px' }}>
        <Kpi etiqueta="Custo ao fornecedor" valor={totais.custo} />
        <Kpi etiqueta="Receita das encomendas" valor={totais.receita} />
        <Kpi etiqueta="Lucro real" valor={totais.lucro} cor={totais.lucro >= 0 ? C.success : C.danger} destaque />
        <Kpi etiqueta="Do teu bolso" valor={totais.falta} cor={totais.falta > 0 ? C.warning : C.textMuted}
          nota={totais.falta > 0 ? 'para pagar ao fornecedor' : 'nada a adiantar'} />
      </div>

      {/* Encomendas sem custo ainda */}
      {semFactura.n > 0 && (
        <div style={{ background: '#eff6fc', border: `1px solid ${C.info}55`, borderRadius: '4px', padding: '10px 14px', marginBottom: '14px', fontSize: '12px', color: C.text }}>
          <strong>{semFactura.n} encomenda(s)</strong> com {fmt(semFactura.total)} € de receita ainda <strong>sem factura de fornecedor</strong> — têm receita mas ainda não têm custo, por isso não entram nas contas acima. Entrariam a inflacionar o lucro.
        </div>
      )}

      {/* Uma factura por cartão */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {facturas.map(f => (
          <div key={f.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: C.bg, borderBottom: `1px solid ${C.border}`, flexWrap: 'wrap' }}>
              <strong style={{ fontSize: '14px', color: C.primary }}>Factura {f.numero_fatura || '—'}</strong>
              {f.numero_fatura && <CopyButton text={f.numero_fatura} size={11} title="Copiar nº" />}
              <span style={{ fontSize: '11px', color: C.textMuted }}>
                {f.data_fatura ? new Date(f.data_fatura).toLocaleDateString('pt-PT') : 'sem data'} · {f.unidades} un · {f.nEncomendas} encomenda(s)
              </span>
              {/* A fase manda: em previsão o custo ainda não tem os portes do envio */}
              <span title={f.ehFinal
                ? 'Factura definitiva — já inclui os portes do envio.'
                : 'Ainda em previsão: o valor definitivo só se sabe quando os portes forem acrescentados no envio. O lucro aqui é estimado.'}
                style={{ marginLeft: 'auto', fontSize: '9px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.4px', padding: '2px 8px', borderRadius: '8px',
                  background: f.ehFinal ? C.successSoft : '#fff4ce', color: f.ehFinal ? C.success : '#92400e',
                  border: `1px solid ${f.ehFinal ? C.success : '#c07400'}55` }}>
                {f.ehFinal ? '✓ Final' : '⚠ Previsão — custo estimado'}
              </span>
              {/* O caminho do QUANTO para o PORQUE.
                  Este ecra da o lucro da factura inteira. Quando o numero
                  surpreende, a pergunta seguinte e sempre a mesma — que artigo,
                  que cliente, que desconto — e a resposta esta na Analise
                  Factura. Ate agora era sair, mudar de separador e procurar a
                  factura outra vez a mao; era isso que fazia os dois ecras
                  parecerem repetidos em vez de encadeados. */}
              {onVerFactura && (
                <button onClick={() => onVerFactura(f.id)}
                  title="Abrir esta factura linha a linha — artigo, cliente, desconto e margem"
                  style={{ height: '24px', padding: '0 9px', border: `1px solid ${C.border}`, borderRadius: '3px',
                    background: C.surface, color: C.primary, cursor: 'pointer', fontSize: '11px', fontWeight: '700',
                    display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                  Ver linha a linha →
                </button>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '0', borderBottom: `1px solid ${C.border}` }}>
              {/* Lucro */}
              <div style={{ padding: '12px 14px', borderRight: `1px solid ${C.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '10px', fontWeight: '800', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                  <TrendingUp size={11} /> Lucro
                </div>
                <Linha etiqueta="Receita das encomendas" valor={f.receita} />
                <Linha etiqueta="Custo da factura" valor={-f.custo} />
                <div style={{ borderTop: `1px solid ${C.border}`, marginTop: '5px', paddingTop: '5px' }}>
                  <Linha etiqueta="Lucro real" valor={f.lucro} forte
                    nota={f.margem != null ? `${f.margem.toFixed(1)}% da receita` : null} />
                </div>
              </div>

              {/* Tesouraria */}
              <div style={{ padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '10px', fontWeight: '800', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                  <Wallet size={11} /> Tesouraria
                </div>
                <Linha etiqueta="Valor dos clientes" valor={f.dosClientes}
                  nota={f.porCobrar > 0.01 ? `falta cobrar ${fmt(f.porCobrar)} €` : 'tudo cobrado'} />
                <Linha etiqueta="Custo da factura" valor={-f.custo} />
                <div style={{ borderTop: `1px solid ${C.border}`, marginTop: '5px', paddingTop: '5px' }}>
                  <Linha
                    etiqueta={f.falta > 0 ? 'Falta pôr do teu bolso' : 'Sobra depois de pagar'}
                    valor={f.falta > 0 ? f.falta : -f.falta}
                    forte
                    cor={f.falta > 0 ? C.warning : C.success}
                  />
                  {/* Só há contas para explicar quando houve compra para a conta
                      interna. Sem ela, o lucro é o lucro — não há nada a destrinçar. */}
                  {f.linhasCatia.length > 0 && (
                    <button onClick={() => setDetalhe(f)}
                      style={{ marginTop: '6px', width: '100%', padding: '5px 8px', border: `1px solid ${C.border}`, borderRadius: '3px', background: C.surface, color: C.primary, cursor: 'pointer', fontSize: '11px', fontWeight: '700' }}>
                      Ver as contas
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* A Cátia explica a diferença entre as duas colunas */}
            {f.parteCatia > 0 && (
              <div style={{ padding: '9px 14px', background: '#faf9ff', borderBottom: `1px solid ${C.border}`, fontSize: '11.5px', color: C.text, display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                <Home size={13} color="#5b21b6" style={{ flexShrink: 0, marginTop: '1px' }} />
                <span>
                  <strong>{fmt(f.parteCatia)} €</strong> desta factura são mercadoria da conta interna.
                  Entram no lucro (ela compra ao custo, logo não o altera) mas <strong>não entram em dinheiro</strong> —
                  é por isso que a tesouraria fica {fmt(f.parteCatia)} € abaixo do lucro. Esse valor só volta quando a mercadoria for vendida nos marketplaces.
                </span>
              </div>
            )}

            <details>
              <summary style={{ padding: '7px 14px', fontSize: '11px', color: C.textMuted, cursor: 'pointer', fontWeight: '600' }}>
                Ver as {f.nEncomendas} encomendas
              </summary>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11.5px' }}>
                <tbody>
                  {f.detalhe.map(d => (
                    <tr key={d.numero} style={{ borderTop: `1px solid ${C.border}` }}>
                      <td style={{ padding: '5px 14px', color: C.text, fontWeight: '600' }}>
                        {d.numero}
                        {d.interno && <span style={{ marginLeft: 5, fontSize: 9, fontWeight: 800, color: '#5b21b6' }}>INTERNO</span>}
                      </td>
                      <td style={{ padding: '5px 8px', color: C.textMuted }}>{d.nome}</td>
                      <td style={{ padding: '5px 8px', color: C.textMuted, whiteSpace: 'nowrap' }}>
                        {d.interno ? 'conta interna' : d.pago === 2 ? '100% pago' : d.pago === 1 ? '50% pago' : 'sem pagamento'}
                      </td>
                      <td style={{ padding: '5px 14px', textAlign: 'right', fontWeight: '700', color: C.text }}>{fmt(d.base)} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          </div>
        ))}
      </div>

      {/* ── As contas, em quatro linhas ── */}
      {detalhe && (
        <div onClick={() => setDetalhe(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: C.surface, borderRadius: '8px', maxWidth: '520px', width: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
            <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong style={{ fontSize: '15px', color: C.text }}>Contas da factura {detalhe.numero_fatura}</strong>
              <button onClick={() => setDetalhe(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: C.textMuted, lineHeight: 1 }}>×</button>
            </div>

            <div style={{ padding: '16px 18px', fontSize: '13.5px', color: C.text }}>
              <Conta etiqueta="A factura é" valor={detalhe.custo} />
              <Conta etiqueta="Valor dos clientes" valor={detalhe.dosClientes} />
              <div style={{ borderTop: `2px solid ${C.border}`, marginTop: '8px', paddingTop: '10px' }}>
                <Conta
                  etiqueta={detalhe.falta > 0 ? 'Tens de pôr do bolso' : 'Sobrou'}
                  valor={detalhe.falta > 0 ? detalhe.falta : -detalhe.falta}
                  cor={detalhe.falta > 0 ? C.warning : C.success}
                  grande
                />
              </div>

              {/* PORQUE falta dinheiro. Há sempre duas causas possíveis e a
                  conta interna nem sempre é a maior — mostrar só essa deixava
                  o número sem explicação. */}
              {(() => {
                const externos = detalhe.detalhe.filter(d => !d.interno)
                  .sort((a, b) => b.porCobrar - a.porCobrar)
                if (externos.length === 0) return null
                return (
                  <div style={{ margin: '14px 0 0', padding: '12px 13px', background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: '5px' }}>
                    <p style={{ margin: '0 0 8px', fontSize: '11px', fontWeight: '800', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      De onde vem o valor dos clientes
                    </p>
                    {externos.map(d => (
                      <div key={d.numero} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '12.5px', padding: '3px 0' }}>
                        <span style={{ color: C.text }}>
                          <strong>{d.numero}</strong> {d.nome?.split(' ')[0]}
                          {/* Entre parênteses o que ainda falta cobrar — conta na
                              mesma para o total, mas convém ter à vista. */}
                          {d.porCobrar > 0.01 && (
                            <span style={{ color: C.warning, fontWeight: '600' }}>
                              {' '}({d.pago === 1 ? 'só pagou 50%' : 'não pagou'} — falta {fmt(d.porCobrar)} €)
                            </span>
                          )}
                        </span>
                        <strong style={{ whiteSpace: 'nowrap' }}>{fmt(d.base)} €</strong>
                      </div>
                    ))}
                    <div style={{ borderTop: `1px solid ${C.border}`, marginTop: '7px', paddingTop: '7px', fontSize: '12.5px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <strong>Total dos clientes</strong>
                        <strong>{fmt(detalhe.dosClientes)} €</strong>
                      </div>
                      {detalhe.porCobrar > 0.01 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', color: C.warning, fontSize: '11.5px', marginTop: '2px' }}>
                          <span>ainda por cobrar</span>
                          <span style={{ fontWeight: '700' }}>{fmt(detalhe.porCobrar)} €</span>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })()}

              {/* O que foi comprado para a conta interna */}
              {detalhe.linhasCatia.length > 0 && (
                <div style={{ margin: '10px 0 0', padding: '11px 13px', background: '#faf9ff', border: '1px solid #5b21b633', borderRadius: '5px' }}>
                  {/* Soma das LINHAS de compra, não o valor da encomenda dela: a
                      encomenda traz portes por cima do custo e os dois números
                      lado a lado não fechavam à vista. */}
                  <Conta etiqueta="Comprado para a Cátia"
                    valor={detalhe.linhasCatia.reduce((s, l) => s + l.total, 0)} cor="#5b21b6" />
                  <div style={{ marginTop: '7px', paddingTop: '7px', borderTop: '1px solid #5b21b622' }}>
                    {detalhe.linhasCatia.map((l, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', fontSize: '12px', padding: '2px 0', color: C.textMuted }}>
                        <span><strong style={{ color: C.text }}>{l.qtd} un</strong> {l.produto}</span>
                        <span style={{ whiteSpace: 'nowrap' }}>{fmt(l.custoUn)} € · <strong style={{ color: C.text }}>{fmt(l.total)} €</strong></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <p style={{ margin: '12px 0 0', fontSize: '11.5px', color: C.textMuted, lineHeight: 1.6 }}>
                Os clientes contam pelo valor total, mesmo os que ainda só pagaram 50% — quem pagou metade não
                desaparece, e se desaparecer a mercadoria fica cá, comprada a metade do preço.
                {detalhe.parteCatia > 0 && ' O que fica mesmo a descoberto é a conta interna: aí nunca entra dinheiro por esta via, só quando a mercadoria for vendida nos marketplaces.'}
                {!detalhe.ehFinal && <><br /><strong style={{ color: '#92400e' }}>Atenção:</strong> esta factura ainda está em previsão — o custo muda quando lançares o valor final com portes.</>}
              </p>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: '14px', padding: '10px 14px', background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '11px', color: C.textMuted, lineHeight: 1.7 }}>
        <AlertTriangle size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
        <strong>Como ler:</strong> o <strong>lucro</strong> diz quanto se ganhou; a <strong>tesouraria</strong> diz se o dinheiro dos clientes chega para pagar a factura.
        Podem apontar em sentidos opostos — uma factura com bom lucro pode exigir dinheiro do bolso se muita mercadoria for da conta interna ou se os clientes ainda só pagaram 50%.
        O IVA fica de fora dos dois lados: é o que é tributado, e o IVA cobrado ao cliente não é dinheiro teu.
      </div>
    </div>
  )
}

const btnSec = { display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 11px', border: `1px solid ${C.border}`, borderRadius: '4px', background: C.surface, color: C.text, cursor: 'pointer', fontSize: '11.5px', fontWeight: '600' }
const btnPri = { display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 11px', border: 'none', borderRadius: '4px', background: C.primary, color: '#fff', cursor: 'pointer', fontSize: '11.5px', fontWeight: '700' }

function Kpi({ etiqueta, valor, cor, nota, destaque }) {
  return (
    <div style={{ background: C.surface, border: `${destaque ? 2 : 1}px solid ${destaque ? (cor || C.border) : C.border}`, borderRadius: '6px', padding: '11px 13px' }}>
      <div style={{ fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{etiqueta}</div>
      <div style={{ fontSize: destaque ? '20px' : '17px', fontWeight: '800', color: cor || C.text, marginTop: '2px' }}>{fmt(valor)} €</div>
      {nota && <div style={{ fontSize: '10px', color: C.textMuted }}>{nota}</div>}
    </div>
  )
}

function Conta({ etiqueta, valor, cor, grande }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '10px', padding: '3px 0' }}>
      <span style={{ fontSize: grande ? '14px' : '13px', fontWeight: grande ? '800' : '600', color: C.text }}>{etiqueta}</span>
      <span style={{ fontSize: grande ? '22px' : '16px', fontWeight: '800', color: cor || C.text, whiteSpace: 'nowrap' }}>{fmt(valor)} €</span>
    </div>
  )
}

function Linha({ etiqueta, valor, forte, cor, nota }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', padding: '2px 0' }}>
      <span style={{ fontSize: forte ? '12.5px' : '11.5px', fontWeight: forte ? '700' : '400', color: forte ? C.text : C.textMuted }}>
        {etiqueta}
        {nota && <span style={{ fontSize: '10px', color: C.textMuted, fontWeight: '400' }}> · {nota}</span>}
      </span>
      <span style={{ fontSize: forte ? '15px' : '12.5px', fontWeight: forte ? '800' : '600', color: cor || (forte ? (valor >= 0 ? C.success : C.danger) : C.text), whiteSpace: 'nowrap' }}>
        {valor < 0 ? '−' : ''}{fmt(Math.abs(valor))} €
      </span>
    </div>
  )
}
